import { z } from 'zod';

/**
 * A playbook suggested edit: a pending AI-proposed change attached to one
 * playbook entity (playbook, category, rule, or position), awaiting accept or
 * reject. Exactly one per entity, stored as JSONB on the row (`suggestedEdit`
 * column) or on the position element inside the rule's `positions` array.
 *
 * Kinds:
 * - `add`: the entity itself is the proposal. A real row or element with no
 *   accepted version underneath.
 * - `delete`: proposes removing an existing entity. It stays in effect until
 *   accepted.
 * - `change`: proposes new field values (`patch`), a new location (`move`), or
 *   both. The two parts resolve independently, so each carries its own
 *   provenance.
 *
 * The union keeps invalid states unrepresentable: `add` and `delete` cannot
 * coexist with anything; only `patch` and `move` coexist, inside `change`.
 *
 * Real columns always hold accepted truth for existing entities; pending values
 * live only in this blob. `add` rows are the exception by nature.
 *
 * `patch.value` uses presence, not null, to mean "proposed": a present
 * `aiGuidance: null` means "clear the guidance". The op wire convention (null
 * means unchanged, empty string means clear) is translated by the suggestion
 * writer; storage never carries the wire convention.
 */

export const PlaybookSuggestionSourceSchema = z.discriminatedUnion('type', [
  z.object({
    messageId: z.string(),
    threadId: z.string(),
    type: z.literal('ai-chat'),
  }),
]);
export type PlaybookSuggestionSource = z.infer<typeof PlaybookSuggestionSourceSchema>;

/**
 * Provenance carried by every suggestion, and by every part of a `change`. The
 * `authorId`-beside-`source` split mirrors the document editor's suggested
 * edits (`BaseEdit.authorId` + `SuggestionSourceSchema`).
 */
export const PlaybookSuggestionMetaSchema = z.object({
  /** The user whose chat produced the suggestion. */
  authorId: z.string(),
  rationale: z.string(),
  source: PlaybookSuggestionSourceSchema,
  suggestedAt: z.iso.datetime(),
});
export type PlaybookSuggestionMeta = z.infer<typeof PlaybookSuggestionMetaSchema>;

export const SUGGESTED_EDIT_KINDS = {
  add: 'add',
  change: 'change',
  delete: 'delete',
} as const;
export type SuggestedEditKind = (typeof SUGGESTED_EDIT_KINDS)[keyof typeof SUGGESTED_EDIT_KINDS];

/** The resolvable parts of a suggestion: what a reviewer accepts or rejects one at a time. */
export const SUGGESTED_EDIT_PARTS = {
  add: 'add',
  delete: 'delete',
  move: 'move',
  patch: 'patch',
} as const;
export type SuggestedEditPart = (typeof SUGGESTED_EDIT_PARTS)[keyof typeof SUGGESTED_EDIT_PARTS];

const AddSuggestedEditSchema = PlaybookSuggestionMetaSchema.extend({
  type: z.literal(SUGGESTED_EDIT_KINDS.add),
});
const DeleteSuggestedEditSchema = PlaybookSuggestionMetaSchema.extend({
  type: z.literal(SUGGESTED_EDIT_KINDS.delete),
});

const CHANGE_NEEDS_A_PART = { message: 'A change must carry a patch, a move, or both.' };
const PATCH_NEEDS_A_FIELD = { message: 'A patch must propose at least one field.' };

// ----- Playbook: patch only (title, description) -----------------------------

export const PlaybookPatchValueSchema = z
  .object({ description: z.string(), title: z.string() })
  .partial()
  .refine(getHasProposedField, PATCH_NEEDS_A_FIELD);
export const PlaybookPatchSchema = PlaybookSuggestionMetaSchema.extend({
  value: PlaybookPatchValueSchema,
});
export const PlaybookSuggestedEditSchema = z.object({
  patch: PlaybookPatchSchema,
  type: z.literal(SUGGESTED_EDIT_KINDS.change),
});
export type PlaybookSuggestedEdit = z.infer<typeof PlaybookSuggestedEditSchema>;

// ----- Category: name patch, rank move ---------------------------------------

export const CategoryPatchValueSchema = z
  .object({ name: z.string() })
  .partial()
  .refine(getHasProposedField, PATCH_NEEDS_A_FIELD);
export const CategoryPatchSchema = PlaybookSuggestionMetaSchema.extend({
  value: CategoryPatchValueSchema,
});
export const CategoryMoveSchema = PlaybookSuggestionMetaSchema.extend({
  rank: z.string().min(1),
});
export const CategorySuggestedEditSchema = z.discriminatedUnion('type', [
  AddSuggestedEditSchema,
  DeleteSuggestedEditSchema,
  z
    .object({
      move: CategoryMoveSchema.optional(),
      patch: CategoryPatchSchema.optional(),
      type: z.literal(SUGGESTED_EDIT_KINDS.change),
    })
    .refine(getHasChangePart, CHANGE_NEEDS_A_PART),
]);
export type CategorySuggestedEdit = z.infer<typeof CategorySuggestedEditSchema>;

// ----- Rule: title patch, (categoryId, rank) move -----------------------------

export const RulePatchValueSchema = z
  .object({ title: z.string() })
  .partial()
  .refine(getHasProposedField, PATCH_NEEDS_A_FIELD);
export const RulePatchSchema = PlaybookSuggestionMetaSchema.extend({
  value: RulePatchValueSchema,
});
export const RuleMoveSchema = PlaybookSuggestionMetaSchema.extend({
  categoryId: z.uuid(),
  rank: z.string().min(1),
});
export const RuleSuggestedEditSchema = z.discriminatedUnion('type', [
  AddSuggestedEditSchema,
  DeleteSuggestedEditSchema,
  z
    .object({
      move: RuleMoveSchema.optional(),
      patch: RulePatchSchema.optional(),
      type: z.literal(SUGGESTED_EDIT_KINDS.change),
    })
    .refine(getHasChangePart, CHANGE_NEEDS_A_PART),
]);
export type RuleSuggestedEdit = z.infer<typeof RuleSuggestedEditSchema>;

// ----- Position: ruleText / aiGuidance patch, rank move -----------------------

export const PositionPatchValueSchema = z
  .object({ aiGuidance: z.string().nullable(), ruleText: z.string() })
  .partial()
  .refine(getHasProposedField, PATCH_NEEDS_A_FIELD);
export const PositionPatchSchema = PlaybookSuggestionMetaSchema.extend({
  value: PositionPatchValueSchema,
});
export const PositionMoveSchema = PlaybookSuggestionMetaSchema.extend({
  rank: z.string().min(1),
});
export const PositionSuggestedEditSchema = z.discriminatedUnion('type', [
  AddSuggestedEditSchema,
  DeleteSuggestedEditSchema,
  z
    .object({
      move: PositionMoveSchema.optional(),
      patch: PositionPatchSchema.optional(),
      type: z.literal(SUGGESTED_EDIT_KINDS.change),
    })
    .refine(getHasChangePart, CHANGE_NEEDS_A_PART),
]);
export type PositionSuggestedEdit = z.infer<typeof PositionSuggestedEditSchema>;

/** Any entity-level suggested edit, for helpers that only look at the kind. */
export type AnySuggestedEdit =
  | PlaybookSuggestedEdit
  | CategorySuggestedEdit
  | RuleSuggestedEdit
  | PositionSuggestedEdit;

/**
 * The shape shared by every entity's suggested edit, for helpers that only
 * look at the kind and which parts are present. A playbook `change` has no
 * `move` key at all, which this optional key absorbs.
 */
export type SuggestedEditShape =
  | { type: typeof SUGGESTED_EDIT_KINDS.add }
  | { type: typeof SUGGESTED_EDIT_KINDS.delete }
  | { type: typeof SUGGESTED_EDIT_KINDS.change; move?: unknown; patch?: unknown };

// ----- Accepted-truth helpers ------------------------------------------------

/**
 * The narrowest shape the helpers below need. Rows carry `suggestedEdit` as a
 * nullable column; position elements carry it as an optional key; GraphQL
 * results carry it as an untyped JSON scalar. `unknown` is what lets all three
 * fit, so the kind is read through `getSuggestedEditKind` rather than off the
 * property.
 */
export interface WithSuggestedEdit {
  suggestedEdit?: unknown;
}

const SuggestedEditKindHolderSchema = z.object({ type: z.enum(SUGGESTED_EDIT_KINDS) });

/** The kind of the entity's pending suggestion, or null when it has none (or an unreadable one). */
export function getSuggestedEditKind(entity: WithSuggestedEdit): SuggestedEditKind | null {
  const parsed = SuggestedEditKindHolderSchema.safeParse(entity.suggestedEdit);
  return parsed.success ? parsed.data.type : null;
}

export function getIsPendingAdd(entity: WithSuggestedEdit): boolean {
  return getSuggestedEditKind(entity) === SUGGESTED_EDIT_KINDS.add;
}

export function getIsPendingDelete(entity: WithSuggestedEdit): boolean {
  return getSuggestedEditKind(entity) === SUGGESTED_EDIT_KINDS.delete;
}

export function getIsPendingChange(entity: WithSuggestedEdit): boolean {
  return getSuggestedEditKind(entity) === SUGGESTED_EDIT_KINDS.change;
}

/**
 * Whether a pending add is the reviewer's decision, or part of its parent's.
 * A row born with its parent (a rule in a new category, a position in a new
 * rule) carries the `add` flag so accepted truth stays a per-row question,
 * but there is nothing to decide on it: accepting the parent accepts the
 * whole subtree, and accepting a child first is refused. So only the topmost
 * pending add in a subtree is a suggestion. Every count, chip and stop that
 * means "a decision" must use this, not `getIsPendingAdd`.
 */
export function getIsOwnAddSuggestion(
  entity: WithSuggestedEdit,
  parent: WithSuggestedEdit | null
): boolean {
  return getIsPendingAdd(entity) && !(parent && getIsPendingAdd(parent));
}

/**
 * Accepted truth: the entities that exist ignoring every suggested edit. A
 * pending `add` is not there yet; a pending `delete` is still there; a pending
 * `change` is there with its accepted field values (callers read the real
 * columns, never `patch.value`). Everything functional (runs, search, counts)
 * reads through this; only the playbook editor UI and the editing-purpose LLM
 * read see suggestions.
 */
export function filterToAccepted<TEntity extends WithSuggestedEdit>(
  entities: readonly TEntity[]
): TEntity[] {
  return entities.filter(entity => !getIsPendingAdd(entity));
}

/** The narrowest shape `getIsRunnableRule` needs: a rule and its positions. */
export interface RuleWithPositions extends WithSuggestedEdit {
  positions: readonly WithSuggestedEdit[];
}

/**
 * Whether a run covers the rule: it exists in accepted truth and has an
 * accepted position to check a document against. A rule the AI has only
 * suggested does not run until somebody accepts it, and neither does a rule
 * left with no position, or only with ones the AI has suggested. Every place
 * that starts a run, lists a run's rules or rolls up its status reads this, so
 * a rule that does not run never shows as "Not Run" on a document or keeps its
 * playbook out of Passed.
 */
export function getIsRunnableRule(rule: RuleWithPositions): boolean {
  return !getIsPendingAdd(rule) && filterToAccepted(rule.positions).length > 0;
}

export function filterToRunnableRules<TRule extends RuleWithPositions>(
  rules: readonly TRule[]
): TRule[] {
  return rules.filter(getIsRunnableRule);
}

/**
 * The resolvable parts of one suggestion, in the order a reviewer steps
 * through them. A `change` with both a patch and a move is two parts; `add`
 * and `delete` are one each.
 */
export function getSuggestedEditParts(
  suggestedEdit: SuggestedEditShape | null | undefined
): SuggestedEditPart[] {
  if (!suggestedEdit) return [];
  if (suggestedEdit.type === SUGGESTED_EDIT_KINDS.add) return [SUGGESTED_EDIT_PARTS.add];
  if (suggestedEdit.type === SUGGESTED_EDIT_KINDS.delete) return [SUGGESTED_EDIT_PARTS.delete];

  const parts: SuggestedEditPart[] = [];
  if (suggestedEdit.patch) parts.push(SUGGESTED_EDIT_PARTS.patch);
  if (suggestedEdit.move) parts.push(SUGGESTED_EDIT_PARTS.move);
  return parts;
}

function getHasChangePart(change: { move?: unknown; patch?: unknown }) {
  return Boolean(change.patch ?? change.move);
}

function getHasProposedField(value: Record<string, unknown>) {
  return Object.values(value).some(field => field !== undefined);
}
